import { useCallback } from 'react'
import {
  DefaultOptionType,
  Deleted,
  KeyOrFn,
  Tooltip as TooltipOptions,
} from '../types'
import { Typography, Tooltip } from 'antd'
import { alphabeticalCompare } from '@cims-online/shared'

type UseCommonOptionsOptions<T> = {
  /**
   * items to transform into options
   */
  items: T[] | undefined
  /**
   * a callback function to sort options, or false to disable sorting
   *
   * if sorting, you should sort alphabetically
   */
  sort: false | keyof T | ((a: T, b: T) => number)
} & TooltipOptions<KeyOrFn<T, string | undefined | null>> &
  Deleted<OrFn<T, boolean>>

type OrFn<T, U> = U | ((t: T) => U)

type UseDefaultOptionsOptions<T> = UseCommonOptionsOptions<T> & {
  type?: 'default'
  /**
   * a key or function to resolve the value prop for each option
   *
   * this value should be matchable with an associated {@link LabeledValueType.value}
   */
  value: KeyOrFn<T, string | number>
  /**
   * a key or function to resolve the disabled prop for each option
   */
  disabled?: OrFn<T, boolean>
  /**
   * a key or function to resolve the label prop for each option
   *
   * this label should be rendered the same as other associated
   * {@link LabeledValueType.label} options
   */
  label?: KeyOrFn<T, React.ReactNode>
}

type UseCustomOptionsOptions<T> = UseCommonOptionsOptions<T> & {
  type: 'custom'
  /**
   * a function to resolve the select option for any given item
   *
   * for most instances, you should be fine setting the
   * {@link UseDefaultOptionsOptions.value|value} and
   * {@link UseDefaultOptionsOptions.label|label} separately
   *
   * @param item
   * @see {@link DefaultOptionType}
   */
  custom: (item: T) => DefaultOptionType
}

export type UseOptionsOptions<T> = (
  | UseDefaultOptionsOptions<T>
  | UseCustomOptionsOptions<T>
) &
  UseCommonOptionsOptions<T>

/**
 * transforms the supplied items into a list of options compatible
 * with both the Ant Design Select component and the CIMSSelect component
 *
 * @param options {@link UseOptionsOptions}
 */
export function useOptions<T>(options: UseOptionsOptions<T>) {
  const toOption = useCallback(
    (item: T, ..._: any[]) => {
      if (options.type === 'custom') return options.custom(item)
      const { disabled: disable, value, label, tooltip: tip, deleted } = options
      const node =
        typeof label === 'function' ? label(item) : label && item[label]
      const dead =
        typeof deleted === 'function' ? deleted(item) : deleted ?? false
      const tooltip = typeof tip === 'function' ? tip(item) : tip && item[tip]
      const active = dead || !!tooltip
      const disabled = typeof disable === 'function' ? disable(item) : disable
      const option: DefaultOptionType = {
        value:
          typeof value === 'function'
            ? value(item)
            : (item[value] as string | number),
        disabled,
        label: (
          <Tooltip
            title={
              active &&
              (dead
                ? tooltip
                  ? `${tooltip} (Inactive)`
                  : 'Inactive'
                : tooltip ?? undefined)
            }
          >
            <Typography.Text delete={dead} disabled={disabled}>
              {node}
            </Typography.Text>
          </Tooltip>
        ),
      }
      return option
    },
    [options]
  )

  const { sort } = options
  let sortFn
  if (!sort) sortFn = () => 0
  else if (typeof sort === 'function') sortFn = sort
  else
    sortFn = (a: T, b: T) => {
      const A = a[sort]
      const B = b[sort]
      // we're doing this on purpose. a[sort] and b[sort] should work with alphabetical compare
      return alphabeticalCompare<any>(A, B)
    }
  return (options.items ?? []).sort(sortFn).map(toOption)
}
